import { isBefore, isSameMonth, isAfter, subDays, addDays } from 'date-fns'
import { getTenantDates } from '@screens/tenants/tenantsUtils'
import { findLocalisationParents, findLocalisationSubLevels } from '@screens/incident/utils'
import sanitize from '@utils/Sanitizer'

import { EtatDesLieuxTypeEnum } from '@api/enum'

interface EDLData {
  manifestation?: ManifestationResponse
  tenant?: Tenant
  dates?: TenantDates
}

/**
 * filter manifestations to keep only searched term
 * @param manifestations initial manifestation list
 * @param search searched term
 * @returns filtered list
 */
const filterManifestations = (manifestations: OrderedManifestations, search: string) => {
  const all = [...manifestations.incoming, ...manifestations.inProgress, ...manifestations.toBeClosed]

  if (!search) {
    return all
  }

  const filtered = all.filter((m) => sanitize(m.libelle).includes(search))

  return filtered
}

/**
 * find manifestation for specified id
 * @param manifestations manifestation list
 * @param id searched id
 * @returns manifestation found or undefined
 */
const findManifestation = (manifestations: OrderedManifestations, id?: string) => {
  if (!id) {
    return undefined
  }

  const all = [...manifestations.incoming, ...manifestations.inProgress, ...manifestations.toBeClosed]
  const manif = all.find((m) => m.id === id)

  return manif
}

/**
 * filter tenants to keep only searched term
 * @param tenants initial tenant list
 * @param search searched term
 * @returns filtered list
 */
const filterTenants = (tenants: OrderedTenants, search: string) => {
  const all = [...tenants.current, ...tenants.former, ...tenants.incoming]

  if (!search) {
    return all
  }

  const filtered = all.filter((t) => sanitize(t.tenant.nomLocatairePermanent).includes(search))

  return filtered
}

/**
 * find tenant for specified id
 * @param tenants  tenant list
 * @param id searched id
 * @returns tenant found or undefined
 */
const findTenant = (tenants: OrderedTenants, id?: string) => {
  if (!id) {
    return undefined
  }

  const all = [...tenants.current, ...tenants.former, ...tenants.incoming]
  const tenant = all.find((m) => m.tenant.id === id)

  return tenant
}

const findData = (
  edl: EtatDesLieuxResponse,
  manifestations: OrderedManifestations,
  tenants: OrderedTenants
): EDLData => {
  const manifestation = findManifestation(manifestations, edl.manifestationId)
  const tenant = findTenant(tenants, edl.locatairePermanentId)
  const dates = !!tenant ? getTenantDates(tenant.tenant) : undefined
  return { manifestation, tenant, dates }
}

const findDataStartDate = (data: EDLData) =>
  !!data.dates
    ? data.dates.start || new Date()
    : !!data.manifestation
    ? new Date(data.manifestation.dateOuverturePublic)
    : new Date()

const orderTerminatedEDLByMonth = (
  list: EtatDesLieuxResponse[],
  manifestations: OrderedManifestations,
  tenants: OrderedTenants
) => {
  return list
    .reduce((acc, cur) => {
      const data = findData(cur, manifestations, tenants)
      const startDate = findDataStartDate(data)
      const index = acc.findIndex((section) => isSameMonth(new Date(section.section), startDate))
      if (index > -1) {
        acc[index].edl.push(cur)
        return acc
      } else {
        return [
          ...acc,
          {
            section: startDate.toISOString(),
            edl: [cur],
          },
        ]
      }
    }, [] as EDLSection[])
    .sort((a, b) => (isBefore(new Date(a.section), new Date(b.section)) ? 1 : -1))
}

const groupByManifestation = (edl: EtatDesLieuxResponse[]) => {
  return Array.from(new Set(edl.map((e) => e.manifestationId || e.locatairePermanentId).filter(Boolean)))
}

const allStepsEDL = (stepNumberSelected: number, i18n: I18nState): Step[] => {
  return [
    {
      label: i18n.t('screens.edl.stepTitle'),
      isSelected: stepNumberSelected === 1,
      stepNumber: 1,
    },
    {
      label: i18n.t('screens.edlRecap.stepTitle'),
      isSelected: stepNumberSelected === 2,
      stepNumber: 2,
    },
    {
      label: i18n.t('screens.edlSignature.stepTitle'),
      isSelected: stepNumberSelected === 3,
      stepNumber: 3,
    },
  ]
}

const findManifestationName = (id: string, manifestations: OrderedManifestations, tenants: OrderedTenants) => {
  const manifestation = findManifestation(manifestations, id)
  const tenant = findTenant(tenants, id)
  return !!manifestation ? manifestation.libelle : !!tenant ? tenant.tenant.nomLocatairePermanent : ''
}

const incidentsFilteredBySpace = (
  item: LocalisationResponse,
  incidentsList: IncidentResponse[],
  places: LocalisationResponse[]
) => {
  const findParentSpace = incidentsList.map((i) => {
    const space = places.find((p) => p.id === i.localisationId)
    const locations = space && findLocalisationParents(places, space)
    const filteredLocations = !!locations ? locations.filter((l) => l.id === item.id) : []
    return filteredLocations.length > 0
      ? {
          localisations: filteredLocations[0],
          incidentId: i.id,
        }
      : undefined
  })
  const parentSpace = findParentSpace.filter((s) => !!s)
  const incidentsIds = parentSpace.map((s) => (!!s ? s.incidentId : ''))
  return incidentsIds
}

const getEDLTypeTenant = (tenant: Tenant) => {
  const dates = getTenantDates(tenant.tenant)
  if (!!dates.start && isBefore(subDays(dates.start, 15), new Date()) && isAfter(addDays(dates.start, 7), new Date())) {
    return EtatDesLieuxTypeEnum.ENTREE
  }
  if (!!dates.end && isBefore(subDays(dates.end, 7), new Date()) && isAfter(addDays(dates.end, 15), new Date())) {
    return EtatDesLieuxTypeEnum.SORTIE
  }
  return undefined
}

/**
 * Find every spaces for already in Entry EDL for given form
 * @param EDLs all edls
 * @param form current form
 * @returns ids found
 */
const findUniqueSpaces = (EDLs: EtatDesLieuxResponse[], form: EDLForm) => {
  const spacesIds = EDLs.reduce((acc, edl) => {
    // find EDLs IN to get spaces
    if (edl.type === EtatDesLieuxTypeEnum.ENTREE) {
      // must match manif or tenant
      if (form.manifestationId === edl.manifestationId || form.locatairePermanentId === edl.locatairePermanentId) {
        // is entry edl for current form
        acc.push(...edl.espaceIds)
      }
    }

    return acc
  }, [] as string[])

  return Array.from(new Set(spacesIds))
}

const filterIncidentsBySpace = (
  places: LocalisationResponse[],
  incidents: IncidentResponse[],
  incIds: string[],
  spaceIds: string[]
) => {
  const incs = incidents.filter((i) => incIds.includes(i.id))

  return spaceIds.reduce((acc, spaceId) => {
    const space = places.find((s) => s.id === spaceId)

    if (space) {
      const locs = [space.id, ...findLocalisationSubLevels(places, space).map((l) => l.id)]
      acc.push(...incs.filter((inc) => !!inc.localisationId && locs.includes(inc.localisationId)))
    }
    return acc
  }, [] as IncidentResponse[])
}

export {
  filterManifestations,
  findManifestation,
  filterTenants,
  findTenant,
  orderTerminatedEDLByMonth,
  allStepsEDL,
  groupByManifestation,
  findManifestationName,
  incidentsFilteredBySpace,
  getEDLTypeTenant,
  findData,
  findUniqueSpaces,
  findDataStartDate,
  filterIncidentsBySpace,
}
